import React from "react";
import { Star, Award, Eye, Clock, Users, Shield } from "lucide-react";
import { LegalLayout, LegalSection, CheckList } from "./legal/LegalLayout";

const standards = [
  { category: "Professional Qualifications", icon: Award, items: [
    "Texas DPS licensed security professionals",
    "Extensive background checks and security clearance",
    "Specialized training in remote monitoring technology",
    "Ongoing education in virtual security best practices",
    "HOA community security expertise",
    "Emergency response and law enforcement coordination training",
  ] },
  { category: "Monitoring Excellence", icon: Eye, items: [
    "24/7/365 continuous monitoring coverage",
    "Maximum 30-second response time to security alerts",
    "Multi-camera simultaneous monitoring capability",
    "Advanced threat detection and pattern recognition",
    "Real-time incident documentation and reporting",
    "Direct law enforcement communication protocols",
  ] },
  { category: "Service Dedication", icon: Users, items: [
    "One Virtual Guard exclusively assigned per community",
    "Minimum 6-month assignment for pattern learning",
    "Personal familiarity with residents and property rhythms",
    "Consistent point of contact for HOA boards",
    "Proactive security recommendations and improvements",
    "Community-specific security protocols and procedures",
  ] },
  { category: "Technology Standards", icon: Shield, items: [
    "High-definition IP camera systems (minimum 4K resolution)",
    "Advanced license plate recognition technology",
    "Encrypted data transmission and secure storage",
    "Redundant internet connectivity and backup power systems",
    "Cloud-based security reporting and dashboard access",
    "Mobile alert systems for immediate incident notification",
  ] },
  { category: "Response Protocols", icon: Clock, items: [
    "Immediate assessment of all security alerts within 30 seconds",
    "Direct law enforcement dispatch for verified threats",
    "Real-time communication with on-site contacts",
    "Comprehensive incident documentation with timestamps",
    "Follow-up reporting within 24 hours of any incident",
    "Monthly security analysis and trend reporting",
  ] },
];

const certifications = [
  { title: "Texas DPS License", number: "#B30959201", description: "State-licensed security company" },
  { title: "Professional Standards", description: "ASIS International security management standards" },
  { title: "Insurance Coverage", description: "Comprehensive professional liability and bonding" },
  { title: "Technology Certified", description: "Advanced security technology integration" },
];

const guarantees = [
  { icon: Clock, title: "30-Second Response", body: "Guaranteed maximum 30-second response time to all security alerts and incidents from your property." },
  { icon: Users, title: "Dedicated Coverage", body: "One Virtual Guard exclusively assigned to your community — never shared with other properties." },
  { icon: Shield, title: "Professional Standards", body: "All Virtual Guards meet Texas DPS licensing requirements and complete specialized community security training." },
];

const VirtualGuardStandards = () => (
  <LegalLayout
    eyebrow="Excellence Standards"
    eyebrowIcon={Star}
    title="Virtual Guard Standards"
    subtitle="Our commitment to excellence in virtual security. Every Redline Virtual Guard meets rigorous professional standards designed specifically for HOA community protection."
    maxWidth="max-w-6xl"
  >
    <div className="space-y-6">
      <LegalSection title="The Redline Difference">
        <p>Unlike queue-based patrol systems, our dedicated Virtual Guards provide exclusive coverage to your community. Every guard is held to the highest professional and performance standards in the industry.</p>
      </LegalSection>

      {standards.map((s) => (
        <LegalSection key={s.category} icon={s.icon} title={s.category}>
          <CheckList color="green" cols={2} items={s.items} />
        </LegalSection>
      ))}

      {/* Certifications */}
      <div className="pt-6">
        <h2 className="font-head text-3xl font-bold text-[#1A1A1A] mb-3">Certifications & Compliance</h2>
        <p className="text-[#1A1A1A]/65 mb-8 max-w-3xl">Redline Monitoring Services Inc. maintains all required certifications and exceeds industry standards for virtual security services.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#1A1A1A]/10 border border-[#1A1A1A]/10">
          {certifications.map((c) => (
            <div key={c.title} className="bg-white p-7 text-center">
              <span className="w-12 h-12 bg-[#D32F2F]/10 flex items-center justify-center mx-auto mb-4"><Award className="w-6 h-6 text-[#D32F2F]" /></span>
              <h3 className="font-head text-base font-bold text-[#1A1A1A] mb-1">{c.title}</h3>
              {c.number && <div className="text-[#D32F2F] font-bold text-sm mb-2">{c.number}</div>}
              <p className="text-sm text-[#1A1A1A]/60">{c.description}</p>
            </div>
          ))}
        </div>
      </div>

      <LegalSection title="Our Service Guarantees">
        <div className="grid lg:grid-cols-3 gap-8">
          {guarantees.map((g) => (
            <div key={g.title}>
              <span className="w-12 h-12 bg-[#D32F2F]/10 flex items-center justify-center mb-5"><g.icon className="w-6 h-6 text-[#D32F2F]" /></span>
              <h3 className="font-head text-xl font-bold text-[#1A1A1A] mb-3">{g.title}</h3>
              <p className="text-[#1A1A1A]/65 leading-relaxed">{g.body}</p>
            </div>
          ))}
        </div>
      </LegalSection>

      <LegalSection title="Quality Commitment Statement" tone="accent">
        <p className="text-xl text-white/90 leading-relaxed">
          "At Redline Monitoring Services Inc., we understand that your HOA community deserves more than generic patrol services. Our Virtual Guard Standards represent our unwavering commitment to providing dedicated, professional, and reliable security coverage that treats your property as if it were our own family's home."
        </p>
        <div className="border-t border-white/15 pt-6 mt-2">
          <p className="font-bold text-white">The Redline Team</p>
          <p className="text-white/60">Redline Monitoring Services Inc.</p>
          <p className="text-sm text-white/40 mt-1">Texas DPS License #B30959201</p>
        </div>
      </LegalSection>
    </div>
  </LegalLayout>
);

export default VirtualGuardStandards;
